import React, { useEffect, useState, useCallback } from "react";
import { openWhatsApp } from "../../lib/whatsapp";
import axios from "axios";
import {
  CalendarClock, Plus, Trash2, Pencil, Save, X, Loader2, Users, Upload, Eye, EyeOff,
  ExternalLink, ChevronDown, ChevronUp, MessageCircle,
} from "lucide-react";
import { toast } from "../../hooks/use-toast";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("admin_token")}` });

const EMPTY = {
  title: "",
  subtitle: "",
  description: "",
  image_url: "",
  active_from: new Date().toISOString().slice(0, 10),
  active_to: new Date().toISOString().slice(0, 10),
  day_start_time: "10:00",
  day_end_time: "19:00",
  slot_duration_min: 120,
  seats_per_slot: 1,
  price: 0,
  currency: "EUR",
  external_url: "",
  organizer: "Il Pozzo dei Giganti",
  confirmation_subject: "Prenotazione confermata · {event_title}",
  confirmation_message:
    "Ciao {name},\nla tua prenotazione per {event_title} il {date} alle {time} è confermata.\n\nTi aspettiamo!\n\nIl Pozzo dei Giganti",
  is_active: true,
};

export default function ExperiencesPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null); // { ...event } | { ...EMPTY }
  const [bookingsFor, setBookingsFor] = useState(null); // event id showing bookings

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/experiences`, { headers: authHeaders() });
      setItems(data.events || []);
    } catch (e) {
      toast({ title: "Errore caricamento eventi", description: e?.response?.data?.detail || e.message });
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const startEdit = (evt) => setForm({ ...EMPTY, ...evt });
  const startNew = () => setForm({ ...EMPTY });
  const cancel = () => setForm(null);

  const save = async () => {
    if (!form.title.trim()) return toast({ title: "Titolo mancante" });
    try {
      if (form.id) {
        await axios.put(`${API}/admin/experiences/${form.id}`, form, { headers: authHeaders() });
        toast({ title: "Evento aggiornato" });
      } else {
        await axios.post(`${API}/admin/experiences`, form, { headers: authHeaders() });
        toast({ title: "Evento creato" });
      }
      setForm(null);
      await load();
    } catch (e) {
      toast({ title: "Errore salvataggio", description: e?.response?.data?.detail || e.message });
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare questo evento e tutte le sue prenotazioni?")) return;
    try {
      await axios.delete(`${API}/admin/experiences/${id}`, { headers: authHeaders() });
      toast({ title: "Evento eliminato" });
      await load();
    } catch (e) {
      toast({ title: "Errore", description: e?.response?.data?.detail || e.message });
    }
  };

  const uploadImage = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await axios.post(`${API}/upload`, fd, {
        headers: { ...authHeaders(), "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, image_url: data.url }));
      toast({ title: "Foto caricata" });
    } catch (e) {
      toast({ title: "Errore upload", description: e?.response?.data?.detail || e.message });
    }
  };

  const toggleActive = async (evt) => {
    try {
      await axios.put(`${API}/admin/experiences/${evt.id}`, { ...evt, is_active: !evt.is_active }, { headers: authHeaders() });
      await load();
    } catch (e) {
      toast({ title: "Errore", description: e?.response?.data?.detail || e.message });
    }
  };

  return (
    <div data-testid="experiences-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <CalendarClock className="h-5 w-5 text-[#4a1c17]" />
          <h2 className="font-serif text-xl font-bold text-[#2b1410]">Eventi</h2>
        </div>
        <button
          onClick={startNew}
          className="inline-flex items-center gap-1.5 rounded-full bg-[#4a1c17] hover:bg-[#2b1410] text-[#f7ebdf] px-3.5 py-2 text-sm font-medium"
          data-testid="new-experience-btn"
        >
          <Plus className="h-4 w-4" /> Nuovo evento
        </button>
      </div>

      {loading ? (
        <div className="py-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#4a1c17]" /></div>
      ) : items.length === 0 ? (
        <p className="text-center text-[#7c5a4e] py-10 italic">Nessun evento configurato. Crea il primo con "Nuovo evento".</p>
      ) : (
        <div className="grid gap-3">
          {items.map((evt) => (
            <EventRow key={evt.id} evt={evt} onEdit={() => startEdit(evt)} onDelete={() => remove(evt.id)} onToggle={() => toggleActive(evt)} onShowBookings={() => setBookingsFor(evt.id === bookingsFor ? null : evt.id)} expanded={bookingsFor === evt.id} />
          ))}
        </div>
      )}

      {form && (
        <EventFormModal form={form} setForm={setForm} onSave={save} onCancel={cancel} onImageUpload={uploadImage} />
      )}
    </div>
  );
}

function EventRow({ evt, onEdit, onDelete, onToggle, onShowBookings, expanded }) {
  const today = new Date().toISOString().slice(0, 10);
  const past = evt.active_to < today;
  const upcoming = evt.active_from > today;
  const now = !past && !upcoming;
  const badge = past ? { txt: "Passato", cls: "bg-gray-100 text-gray-500" }
    : now ? { txt: "In corso", cls: "bg-red-500 text-white animate-pulse" }
    : { txt: "Prossimo", cls: "bg-amber-100 text-amber-800" };

  return (
    <div className="rounded-xl border border-[#e8dcc8] bg-white shadow-sm overflow-hidden" data-testid={`experience-row-${evt.id}`}>
      <div className="flex gap-3 p-3">
        {evt.image_url ? (
          <img src={evt.image_url} alt="" className="w-20 h-20 rounded-lg object-cover shrink-0" />
        ) : (
          <div className="w-20 h-20 rounded-lg bg-[#faf3ee] flex items-center justify-center shrink-0">
            <CalendarClock className="h-6 w-6 text-[#a08a7e]" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full ${badge.cls}`}>{badge.txt}</span>
            {!evt.is_active && <span className="text-[10px] uppercase font-bold text-red-600">disattivato</span>}
          </div>
          <h3 className="font-serif text-base font-bold text-[#2b1410] leading-tight truncate">{evt.title}</h3>
          <p className="text-xs text-[#7c5a4e] italic truncate">{evt.subtitle}</p>
          <div className="mt-1.5 flex items-center gap-3 text-xs text-[#7c5a4e] flex-wrap">
            <span>{evt.active_from === evt.active_to ? evt.active_from : `${evt.active_from} → ${evt.active_to}`}</span>
            <span>· {evt.day_start_time}–{evt.day_end_time}</span>
            <span>· <b>{evt.slot_duration_min} min</b>/slot</span>
            <span>· <Users className="h-3 w-3 inline" /> {evt.seats_per_slot} pax</span>
            {evt.price > 0 && <span>· <b>{evt.price} {evt.currency}</b></span>}
          </div>
        </div>
        <div className="flex flex-col gap-1.5">
          <button onClick={onEdit} className="p-1.5 rounded-lg hover:bg-[#faf3ee] text-[#4a1c17]" title="Modifica" data-testid={`edit-${evt.id}`}>
            <Pencil className="h-4 w-4" />
          </button>
          <button onClick={onToggle} className="p-1.5 rounded-lg hover:bg-[#faf3ee] text-[#4a1c17]" title={evt.is_active ? "Nascondi" : "Attiva"}>
            {evt.is_active ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
          </button>
          <button onClick={onDelete} className="p-1.5 rounded-lg hover:bg-red-50 text-red-600" title="Elimina">
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>
      <button onClick={onShowBookings} className="w-full text-left px-3 py-2 text-xs text-[#7c5a4e] hover:bg-[#faf3ee] border-t border-[#e8dcc8] flex items-center justify-between">
        <span>Vedi prenotazioni</span>
        {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
      </button>
      {expanded && <BookingsList eventId={evt.id} evt={evt} />}
    </div>
  );
}

function BookingsList({ eventId, evt }) {
  const [bookings, setBookings] = useState(null);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/experiences/${eventId}/bookings`, { headers: authHeaders() })
      .then((r) => setBookings(r.data.bookings || []))
      .catch(() => setBookings([]));
  }, [eventId]);

  const cancel = async (b) => {
    if (!window.confirm(`Annullare la prenotazione di ${b.name}?`)) return;
    setBusy(b.id);
    try {
      await axios.put(`${API}/admin/experiences/bookings/${b.id}`, { status: "cancelled" }, { headers: authHeaders() });
      setBookings((bs) => bs.map((x) => x.id === b.id ? { ...x, status: "cancelled" } : x));
    } catch (e) {
      toast({ title: "Errore", description: e?.response?.data?.detail || e.message });
    } finally { setBusy(null); }
  };

  const waConfirm = async (b) => {
    // Use the per-event confirmation_message template first; if empty, fall
    // back to the global `event_booking` template editable in Admin →
    // Messaggi → Evento · Conferma prenotazione.
    const dt = new Date(b.slot_datetime);
    const day = dt.toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" });
    const hh = dt.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
    const pax = b.participants || 1;
    const totalPrice = (evt?.price || 0) * pax;

    const ctx = {
      name: b.name || "",
      event_title: evt?.title || "",
      date: day,
      time: hh,
      participants: String(pax),
      organizer: evt?.organizer || "Il Pozzo dei Giganti",
      price: totalPrice ? `${totalPrice}` : "",
    };

    let msg;
    const localTemplate = (evt?.confirmation_message || "").trim();
    if (localTemplate) {
      // Legacy per-event template
      msg = localTemplate;
      Object.entries(ctx).forEach(([k, v]) => { msg = msg.split(`{${k}}`).join(String(v)); });
      // Legacy alias {price} without €
      msg = msg.replaceAll("{price}", totalPrice ? `${totalPrice} €` : "");
    } else {
      try {
        const r = await axios.post(
          `${API}/admin/wa-templates/event_booking/preview`,
          { body: null, ctx },
          { headers: authHeaders() },
        );
        msg = r.data?.rendered || "";
      } catch (_) {
        msg = `Ciao ${b.name || ""},\n\nLa tua prenotazione per ${evt?.title || "il nostro evento"} il ${day} alle ${hh} è confermata.`;
      }
    }

    // Normalize phone number
    let phone = (b.phone || "").replace(/\D/g, "");
    if (!phone) return toast({ title: "Numero mancante", description: "Impossibile aprire WhatsApp" });
    if (!phone.startsWith("39") && phone.length === 10) phone = "39" + phone;
    openWhatsApp(phone, msg);
    // Mark as sent
    try {
      await axios.put(`${API}/admin/experiences/bookings/${b.id}`, { whatsapp_notified: true }, { headers: authHeaders() });
      setBookings((bs) => bs.map((x) => x.id === b.id ? { ...x, whatsapp_notified: true, wa_sent_at: new Date().toISOString() } : x));
    } catch (e) {
      // Silent — user has already opened WhatsApp
    }
  };

  if (!bookings) return <div className="p-3"><Loader2 className="h-4 w-4 animate-spin text-[#4a1c17]" /></div>;
  if (bookings.length === 0) return <p className="p-3 text-xs italic text-[#7c5a4e]">Nessuna prenotazione ancora.</p>;

  return (
    <div className="border-t border-[#e8dcc8] bg-[#faf3ee]/50 divide-y divide-[#e8dcc8]">
      {bookings.map((b) => {
        const dt = new Date(b.slot_datetime);
        return (
          <div key={b.id} className={`p-3 flex items-center gap-2 text-sm flex-wrap ${b.status === "cancelled" ? "opacity-50 line-through" : ""}`}>
            <span className="font-mono text-xs bg-white border border-[#e8dcc8] rounded px-1.5 py-0.5">{dt.toLocaleDateString("it-IT")} {dt.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}</span>
            <span className="font-semibold text-[#2b1410]">{b.name}</span>
            <span className="text-xs text-[#7c5a4e]">· {b.participants} pax</span>
            {b.phone && <a href={`tel:${b.phone}`} className="text-xs text-[#4a1c17] underline">{b.phone}</a>}
            {b.email && <span className="text-xs text-[#7c5a4e] truncate max-w-[180px]">{b.email}</span>}
            <div className="ml-auto flex items-center gap-1.5">
              {b.status !== "cancelled" && b.phone && (
                <button
                  onClick={() => waConfirm(b)}
                  data-testid={`exp-booking-wa-${b.id}`}
                  className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold transition-colors ${
                    b.whatsapp_notified
                      ? "bg-purple-100 border border-purple-300 text-purple-800 hover:bg-purple-200"
                      : "bg-green-500 hover:bg-green-600 text-white"
                  }`}
                  title={b.whatsapp_notified ? `Inviato ${b.wa_sent_at ? new Date(b.wa_sent_at).toLocaleString("it-IT") : ""}` : "Invia conferma via WhatsApp"}
                >
                  <MessageCircle className="h-3 w-3" />
                  {b.whatsapp_notified ? "Inviato" : "WhatsApp"}
                </button>
              )}
              {b.status !== "cancelled" && (
                <button onClick={() => cancel(b)} disabled={busy === b.id} className="text-xs text-red-600 hover:underline">Annulla</button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function EventFormModal({ form, setForm, onSave, onCancel, onImageUpload }) {
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={onCancel}>
      <div className="bg-white rounded-2xl w-full max-w-2xl my-8 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-[#4a1c17] text-[#f7ebdf] px-4 py-3 flex items-center justify-between rounded-t-2xl">
          <h3 className="font-serif text-lg font-bold">{form.id ? "Modifica evento" : "Nuovo evento"}</h3>
          <button onClick={onCancel}><X className="h-5 w-5" /></button>
        </div>
        <div className="p-4 space-y-3">
          <F label="Titolo *"><input value={form.title} onChange={(e) => set("title", e.target.value)} className="w-full input" /></F>
          <F label="Sottotitolo"><input value={form.subtitle} onChange={(e) => set("subtitle", e.target.value)} className="w-full input" placeholder="es. con Fera Teatro · Costellazioni Familiari" /></F>
          <F label="Descrizione"><textarea rows={4} value={form.description} onChange={(e) => set("description", e.target.value)} className="w-full input" placeholder="Descrizione ricca dell'evento…" /></F>
          <F label="Foto">
            <div className="flex gap-2 items-center">
              <input type="file" accept="image/*,application/pdf" onChange={(e) => onImageUpload(e.target.files?.[0])} className="text-xs" />
              {form.image_url && <img src={form.image_url} alt="" className="h-12 w-12 rounded-lg object-cover" />}
            </div>
          </F>
          <div className="grid grid-cols-2 gap-3">
            <F label="Data inizio *"><input type="date" value={form.active_from} onChange={(e) => set("active_from", e.target.value)} className="w-full input" /></F>
            <F label="Data fine *"><input type="date" value={form.active_to} onChange={(e) => set("active_to", e.target.value)} className="w-full input" /></F>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <F label="Ora apertura"><input type="time" value={form.day_start_time} onChange={(e) => set("day_start_time", e.target.value)} className="w-full input" /></F>
            <F label="Ora chiusura"><input type="time" value={form.day_end_time} onChange={(e) => set("day_end_time", e.target.value)} className="w-full input" /></F>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <F label="Durata slot (min)"><input type="number" min="15" step="15" value={form.slot_duration_min} onChange={(e) => set("slot_duration_min", parseInt(e.target.value) || 60)} className="w-full input" /></F>
            <F label="Posti per slot"><input type="number" min="1" value={form.seats_per_slot} onChange={(e) => set("seats_per_slot", parseInt(e.target.value) || 1)} className="w-full input" /></F>
            <F label="Prezzo (€)"><input type="number" min="0" step="0.5" value={form.price} onChange={(e) => set("price", parseFloat(e.target.value) || 0)} className="w-full input" /></F>
          </div>
          <F label="Organizzatore"><input value={form.organizer} onChange={(e) => set("organizer", e.target.value)} className="w-full input" /></F>
          <F label="Link esterno (opzionale)"><input value={form.external_url} onChange={(e) => set("external_url", e.target.value)} className="w-full input" placeholder="https://www.ferateatro.it/s/157" /></F>
          <F label="Oggetto email conferma">
            <input value={form.confirmation_subject} onChange={(e) => set("confirmation_subject", e.target.value)} className="w-full input" />
            <p className="text-[10px] text-[#7c5a4e] mt-1">Placeholder disponibili: {"{name}"} {"{event_title}"} {"{date}"} {"{time}"} {"{participants}"} {"{organizer}"} {"{price}"}</p>
          </F>
          <F label="Messaggio email conferma">
            <textarea rows={5} value={form.confirmation_message} onChange={(e) => set("confirmation_message", e.target.value)} className="w-full input" />
          </F>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.is_active} onChange={(e) => set("is_active", e.target.checked)} />
            Attivo (visibile al cliente)
          </label>
        </div>
        <div className="sticky bottom-0 bg-white border-t border-[#e8dcc8] p-3 flex justify-end gap-2 rounded-b-2xl">
          <button onClick={onCancel} className="px-4 py-2 rounded-full border border-[#e8dcc8] text-[#4a1c17]">Annulla</button>
          <button onClick={onSave} className="px-4 py-2 rounded-full bg-[#4a1c17] hover:bg-[#2b1410] text-[#f7ebdf] font-semibold inline-flex items-center gap-1.5" data-testid="save-experience-btn">
            <Save className="h-4 w-4" /> Salva
          </button>
        </div>
      </div>
      <style>{`.input { border: 1px solid #e8dcc8; border-radius: 8px; padding: 8px 12px; font-size: 14px; background: white; } .input:focus { outline: none; border-color: #4a1c17; }`}</style>
    </div>
  );
}

function F({ label, children }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-widest font-semibold text-[#7c5a4e] mb-1">{label}</label>
      {children}
    </div>
  );
}
